import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MovieCard from "./MovieCard";
import { useDispatch, useSelector } from "react-redux";
import { addMovies } from "../store/movieSlice";
import "../style/scrollBar.css";

const GENRES = [
  { id: 1, name: "Action" },
  { id: 18, name: "War" },
  { id: 11, name: "Horror" },
  { id: 15, name: "Science Fiction" },
];

const requestStateFromMovieData = (movieData) =>
  GENRES.reduce((acc, { name }) => {
    acc[name] = {
      status: Array.isArray(movieData?.[name]) ? "success" : "idle",
      error: null,
    };
    return acc;
  }, {});

const LoadingRow = () => (
  <div className="flex overflow-x-auto gap-2 horizontal-scroll">
    {Array.from({ length: 8 }).map((_, index) => (
      <div
        key={index}
        className="flex-shrink-0 w-24 h-36 sm:w-32 sm:h-48 md:w-40 md:h-60 rounded-md animate-pulse bg-gray-700"
      />
    ))}
  </div>
);

const MovieCategories = () => {
  const dispatch = useDispatch();
  const movieData = useSelector((state) => state.movies.moviesByGenre);
  const apiKey = process.env.REACT_APP_WATCHMODE_API_KEY;
  const movieDataRef = useRef(movieData);

  const [requestStateByGenre, setRequestStateByGenre] = useState(() =>
    requestStateFromMovieData(movieData)
  );

  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    movieDataRef.current = movieData;
  }, [movieData]);

  const setGenreState = useCallback((genreName, nextState) => {
    if (!isMountedRef.current) return;

    setRequestStateByGenre((prev) => ({
      ...prev,
      [genreName]: {
        ...prev[genreName],
        ...nextState,
      },
    }));
  }, []);

  const fetchMoviesByGenre = useCallback(
    async ({ id, name }) => {
      if (Array.isArray(movieDataRef.current?.[name])) {
        setGenreState(name, { status: "success", error: null });
        return;
      }

      if (!apiKey) {
        setGenreState(name, {
          status: "error",
          error: "Missing API key. Add WATCHMODE key in .env.",
        });
        return;
      }

      setGenreState(name, { status: "loading", error: null });

      try {
        const response = await fetch(
          `https://api.watchmode.com/v1/list-titles/?apiKey=${apiKey}&genres=${id}`
        );

        if (!response.ok) {
          throw new Error(`Watchmode request failed (${response.status}).`);
        }

        const data = await response.json();
        const titles = Array.isArray(data?.titles) ? data.titles : [];

        dispatch(addMovies({ genre: name, movies: titles }));
        setGenreState(name, { status: "success", error: null });
      } catch (error) {
        setGenreState(name, {
          status: "error",
          error: error?.message || "Unable to load this category right now.",
        });
      }
    },
    [apiKey, dispatch, setGenreState]
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchSequentially = async () => {
      for (const genre of GENRES) {
        if (isCancelled) return;
        await fetchMoviesByGenre(genre);
      }
    };

    fetchSequentially();

    return () => {
      isCancelled = true;
    };
  }, [fetchMoviesByGenre]);

  const categories = useMemo(
    () =>
      GENRES.map((genre) => {
        const movies = movieData?.[genre.name];
        const requestState = requestStateByGenre?.[genre.name];
        return {
          ...genre,
          movies,
          status: requestState?.status || "idle",
          error: requestState?.error || null,
        };
      }),
    [movieData, requestStateByGenre]
  );

  return (
    <div className="px-5 md:px-10 lg:px-20 xl:px-40 bg-categoriesbg space-y-8 py-4 lg:py-10 max-lg:mt-16">
      {categories.map(({ id, name, movies, status, error }) => (
        <div key={name}>
          <h2 className="md:text-xl font-medium text-white capitalize mb-2">
            {name} Movies
          </h2>

          {(status === "idle" || status === "loading") && <LoadingRow />}

          {status === "error" && (
            <div className="bg-red-900/20 border border-red-500/40 rounded-md px-4 py-3 text-red-200 flex items-center justify-between gap-3">
              <p className="text-sm">{error}</p>
              <button
                type="button"
                onClick={() => fetchMoviesByGenre({ id, name })}
                className="bg-red-600 hover:bg-red-700 text-white text-sm px-3 py-1 rounded-md transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {status === "success" && Array.isArray(movies) && movies.length > 0 && (
            <div className="flex overflow-x-auto gap-2 horizontal-scroll">
              {movies
                .filter((movie) => movie?.imdb_id)
                .slice(0, 8)
                .map((movie) => (
                  <MovieCard key={movie.id} query={movie.imdb_id} parameter="i" />
                ))}
            </div>
          )}

          {status === "success" && Array.isArray(movies) && movies.length === 0 && (
            <p className="text-sm text-gray-400 mt-2">No movies found for this category.</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default MovieCategories;
